/**
 * WeeklyDayCard Component
 * Displays a collapsible card for a single day in a weekly schedule
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
  Button,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import {
  DayOfWeek,
  WeeklyDayConfig,
  ScheduleStep,
  StepData,
  Schedule,
  formatDuration,
  calculateEstimatedDuration,
} from '../../types/schedule';
import { Action } from '../../types/action';
import { StepCard } from './StepCard';

interface WeeklyDayCardProps {
  day: DayOfWeek;
  config: WeeklyDayConfig | null | undefined;
  deviceActions: Action[];
  deviceSchedules: Schedule[];
  onToggleEnabled: (enabled: boolean) => void;
  onAddStep: () => void;
  onEditStep: (step: ScheduleStep) => void;
  onDeleteStep: (stepId: string) => void;
  onReorderSteps: (stepIds: string[]) => void;
  onCopyDay: () => void;
  onSettingsClick?: () => void;
  disabled?: boolean;
}

export const WeeklyDayCard: React.FC<WeeklyDayCardProps> = ({
  day,
  config,
  deviceActions,
  deviceSchedules,
  onToggleEnabled,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onReorderSteps,
  onCopyDay,
  onSettingsClick,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Drag sensors for step reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isEnabled = config?.isEnabled ?? true;
  const steps: ScheduleStep[] = config?.steps
    ? Object.entries(config.steps)
        .map(([id, stepData]: [string, StepData]) => ({
          id,
          order: stepData.order ?? 0,
          title: stepData.title ?? '',
          subtitle: stepData.subtitle,
          stepType: stepData.stepType ?? 'action',
          actionId: stepData.actionId,
          scheduleId: stepData.scheduleId,
          scheduledTime: stepData.scheduledTime,
          timer: stepData.timer,
          isSkippable: stepData.isSkippable ?? true,
          completionAction: stepData.completionAction ?? 'tap',
          helpPrompt: stepData.helpPrompt,
          tokenValue: stepData.tokenValue ?? 0,
          isRewardStep: stepData.isRewardStep ?? false,
          createdAt: stepData.createdAt ?? 0,
          updatedAt: stepData.updatedAt ?? 0,
        } as ScheduleStep))
        .sort((a, b) => a.order - b.order)
    : [];
  const stepCount = steps.length;
  const estimatedDuration = stepCount > 0 ? calculateEstimatedDuration(steps) : 0;

  // Helper to get action info for a step
  const getActionInfo = (step: ScheduleStep) => {
    if (step.stepType === 'action' && step.actionId) {
      const action = deviceActions.find(a => a.id === step.actionId);
      return {
        actionImage: action?.thumbnailUrl || action?.pictureUrl || null,
        actionName: action?.name,
      };
    }
    return { actionImage: null, actionName: undefined };
  };

  // Helper to get schedule info for a step
  const getScheduleInfo = (step: ScheduleStep) => {
    if (step.stepType === 'schedule' && step.scheduleId) {
      const schedule = deviceSchedules.find(s => s.id === step.scheduleId);
      return {
        scheduleName: schedule?.name,
        scheduleIcon: schedule?.icon?.emoji ?? undefined,
      };
    }
    return { scheduleName: undefined, scheduleIcon: undefined };
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCopyDay = () => {
    handleMenuClose();
    onCopyDay();
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    onSettingsClick?.();
  };

  // Handle drag end for step reordering
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      // Find old and new indices
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Calculate new order and call parent handler
      const reorderedSteps = arrayMove(steps, oldIndex, newIndex);
      const newStepIds = reorderedSteps.map((s) => s.id);
      onReorderSteps(newStepIds);
    },
    [steps, onReorderSteps]
  );

  return (
    <Card
      sx={{
        mb: 1,
        opacity: isEnabled ? 1 : 0.6,
        backgroundColor: isEnabled ? 'background.paper' : 'action.disabledBackground',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ mr: 1 }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
          {t(`schedules.daysLong.${day}`)}
        </Typography>

        {stepCount > 0 && (
          <Chip
            size="small"
            label={t('schedules.weekly.stepsCount', { count: stepCount })}
            sx={{ mr: 1 }}
          />
        )}

        {estimatedDuration > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {formatDuration(estimatedDuration)}
          </Typography>
        )}

        <Switch
          checked={isEnabled}
          onChange={(e) => {
            e.stopPropagation();
            onToggleEnabled(e.target.checked);
          }}
          disabled={disabled}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />

        <IconButton
          size="small"
          onClick={handleMenuOpen}
          disabled={disabled}
        >
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleCopyDay} disabled={stepCount === 0}>
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedules.weekly.copyDay')}</ListItemText>
          </MenuItem>
          {onSettingsClick && (
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('schedules.settings.title')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Collapse in={expanded && isEnabled}>
        <Divider />
        <CardContent sx={{ py: 1 }}>
          {stepCount === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('schedules.weekly.noStepsForDay')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddStep}
                disabled={disabled}
                size="small"
              >
                {t('schedules.weekly.addStepToDay')}
              </Button>
            </Box>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {steps.map((step) => {
                      const { actionImage, actionName } = getActionInfo(step);
                      const { scheduleName, scheduleIcon } = getScheduleInfo(step);
                      return (
                        <StepCard
                          key={step.id}
                          step={step}
                          onClick={() => onEditStep(step)}
                          onDelete={() => onDeleteStep(step.id)}
                          actionImage={actionImage}
                          actionName={actionName}
                          scheduleName={scheduleName}
                          scheduleIcon={scheduleIcon}
                        />
                      );
                    })}
                  </Box>
                </SortableContext>
              </DndContext>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={onAddStep}
                  disabled={disabled}
                  size="small"
                >
                  {t('schedules.weekly.addStepToDay')}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default WeeklyDayCard;
